import { useState } from 'react';
import { DateTime } from 'luxon';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CampaignDetailsForm from 'components/forms/CampaignDetails/CampaignDetailsForm.tsx';
import Back from 'components/Back';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import { updateCampaignDetails } from 'reducers/campaign/actions.ts';
import { CampaignDetails, CampaignTarget } from 'schemas/CampaignConfig.schema.ts';
import { ApiError } from 'schemas/ApiError.schema.ts';
import useCreateCampaign from 'queries/mutate/createCampaign.ts';
import { API_CREATE_CAMPAIGN_ERROR } from 'constants/errorMessages';
import { ITeamLevelParams } from 'utils/parameterUtils';
import { initialCampaign } from 'constants/campaign.ts';
import useFetchLatestTemplateDetails from 'queries/fetch/fetchLatestTemplateDetails.ts';
import ProgressIndicator from 'components/ProgressIndicator';
import useFetchCampaignTemplatesForTeam from 'queries/fetch/fetchCampaignTemplatesForTeam.ts';
import featureFlags from 'services/featureFlags.ts';
import { TODAY_DATE_TOKEN } from 'schemas/CampaignTemplates.schema.ts';

function NewCampaignKeyDetailsPage() {
  const { teamId } = useParams() as ITeamLevelParams;
  const [error, setError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const teamTemplates = useFetchCampaignTemplatesForTeam({ teamId });
  const campaignTemplate = useFetchLatestTemplateDetails({ teamId, templateId: searchParams.get('template') });
  const templateDetails = teamTemplates.data?.templates.find((t) => t.id === searchParams.get('template'));

  const createCampaign = useCreateCampaign();

  useProtectRoute();

  const approverDisplay = campaignTemplate.data?.['CampaignConfig.Approver.input.display'];
  const approverDefault = campaignTemplate.data?.['CampaignConfig.Approver.input.default']?.trim();

  const managerDisplay = campaignTemplate.data?.['CampaignConfig.Manager.input.display'];
  const managerDefault = campaignTemplate.data?.['CampaignConfig.Manager.input.default']?.trim();

  const reviewerDisplay = campaignTemplate.data?.['CampaignConfig.Reviewer.input.display'];
  const reviewerDefault = campaignTemplate.data?.['CampaignConfig.Reviewer.input.default']?.trim();

  const startDateDefault = campaignTemplate.data?.['CampaignConfig.StartDate.input.default'] === TODAY_DATE_TOKEN
    ? DateTime.now().toFormat('yyyyMMdd')
    : campaignTemplate.data?.['CampaignConfig.StartDate.input.default'];

  const allContactDetailsHiddenAndDefaulted = (
    approverDisplay === 'hidden' && approverDefault
      && managerDisplay === 'hidden' && managerDefault
      && reviewerDisplay === 'hidden' && reviewerDefault
  );

  const messagePlansDefault = campaignTemplate.data?.['CampaignConfig.AvailableRoutingIds.input.default'];

  const handleSuccessfulSubmission = async (campaignDetails: CampaignDetails) => {
    if (!teamId) {
      setError({
        status: 500,
        message: 'Failed to create campaign. Your user is not associated with any teams, please contact a member of the SPI team.',
      });
      return;
    }

    const updatedCampaign = updateCampaignDetails(
      initialCampaign,
      campaignDetails,
    );

    try {
      setIsSubmitting(true);
      const response = await createCampaign.mutateAsync({
        teamId,
        campaign: {
          ...updatedCampaign,
          CampaignConfig: {
            ...updatedCampaign.CampaignConfig,
            ...(
              templateDetails
                ? {
                  template_id: templateDetails.id,
                  template_version: templateDetails.version,
                  ...(
                    allContactDetailsHiddenAndDefaulted
                      ? {
                        Approver: approverDefault.split(','),
                        Manager: managerDefault.split(','),
                        Reviewer: reviewerDefault.split(','),
                      }
                      : {}
                  ),
                  ...(
                    messagePlansDefault ? {
                      AvailableRoutingIds: messagePlansDefault.split(','),
                    }
                      : {}
                  ),
                }
                : {}
            ),
          },
        },
      });

      navigate(
        `/${teamId}/campaign/${response.campaign_details.ID}/key-details/confirmation`,
      );
    } catch {
      setError({ message: API_CREATE_CAMPAIGN_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (featureFlags.campaignTemplates && (!campaignTemplate.isFetched || !teamTemplates.isFetched)) {
    return <ProgressIndicator />;
  }

  return (
    <div>
      <div className="nhsuk-u-margin-bottom-4">
        <Back label="Manage campaigns" backTo="/campaigns" />
      </div>
      <CampaignDetailsForm
        onSuccessfulSubmission={handleSuccessfulSubmission}
        isSubmitting={isSubmitting}
        campaign={{
          ...initialCampaign,
          CampaignConfig: {
            ...initialCampaign.CampaignConfig,
            Type: (campaignTemplate.data?.['CampaignConfig.Type.input.default'] ?? initialCampaign.CampaignConfig.Type) as 'V' | 'A',
            Target: (campaignTemplate.data?.['CampaignConfig.Target.input.default'] ?? initialCampaign.CampaignConfig.Target) as CampaignTarget,
            Name: (campaignTemplate.data?.['CampaignConfig.Name.input.default'] ?? initialCampaign.CampaignConfig.Name),
            StartDate: (startDateDefault ?? initialCampaign.CampaignConfig.StartDate),
            EndDate: (campaignTemplate.data?.['CampaignConfig.EndDate.input.default'] ?? initialCampaign.CampaignConfig.EndDate),
          },
        }}
        template={campaignTemplate.data}
      />
      <ApiErrorDetails apiError={error} />
    </div>
  );
}

export default NewCampaignKeyDetailsPage;
